<template>
  <div class="msdat-instance" :style="instanceStyles">
    <!-- Fixed Header -->
    <header id="the-header" class="position-fixed w-100" data-testid="header">
      <b-container fluid>
        <b-row class="d-flex justify-content-between align-items-center">
          <b-col cols md="1" lg="1" class="main">
            <div>
              <img :src="config?.logo || '@/assets/img/Logo.svg'" alt="Logo" class="img-fluid" />
            </div>
          </b-col>
          <b-col cols md="11" lg="11" class="d-flex justify-content-between align-items-center border-left main mains">
            <div class="main-text">
              <h2 class="main-text">
                <div>{{ config?.name || 'MSDAT Dashboard' }}</div>
                <small>{{ config?.description || '' }}</small>
              </h2>
            </div>
            <div class="user-account">
              <b-dropdown right text="User Account" variant="link" class="m-2">
                <template #button-content>
                  <b-icon-person-circle />
                </template>
                <b-dropdown-item>Profile</b-dropdown-item>
                <b-dropdown-item>Settings</b-dropdown-item>
                <b-dropdown-divider></b-dropdown-divider>
                <b-dropdown-item>Logout</b-dropdown-item>
              </b-dropdown>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </header>

    <!-- Main Content with Fixed Control Panel -->
    <div class="main-content-wrapper">
      <section class="dashboard-content">
        <!-- Fixed Control Panel -->
        <div class="control-panel-container">
          <div class="control-panel">
            <ControlPanel
              :setup="controlPanelSetup"
              :defaultIndicator="defaultIndicator"
              :defaultDataSource="defaultDataSource"
              :defaultLocation="defaultLocation"
              :defaultYear="defaultYear"
              :controlIndex="0"
              @data:options="handleControlPanelUpdate"
            />
          </div>
        </div>

        <!-- Section Tabs -->
        <b-nav tabs>
          <b-nav-item
            :active="selectedSection === 'Indicator Overview'"
            @click="selectedSection = 'Indicator Overview'"
          >
            Indicator Overview
          </b-nav-item>
          <!-- Add more tabs here for other sections as needed -->
        </b-nav>

        <!-- Main Content Area -->
        <div class="main-content">
          <BaseIndicatorOverview
            v-if="selectedSection === 'Indicator Overview'"
            :config="config"
            :controlPanelProps="controlPanelProps"
            @value="handleValueChange"
            @key="handleKeyChange"
            @reset="handleReset"
          />
        </div>
      </section>
    </div>

    <!-- Footer -->
    <footer id="the-footer" class="work-sans">
      <div>Built with <b-icon-heart-fill /> by eHealth4everyone</div>
      <div>
        <span>{{ config?.name }}</span>
        <span class="ml-3">Last Updated {{ new Date().toLocaleDateString() }}</span>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import ControlPanel from '@/components/ControlPanel/GenericPanels/ControlPanel.vue';
import BaseIndicatorOverview from '@/modules/msdat-dashboard/components/sections/indicator-overview/BaseIndicatorOverview.vue';
import IndicatorOverviewConfig from '@/modules/msdat-dashboard/components/sections/indicator-overview/control-panel-config';
import { defaultConfig } from '../utils/configSchema';

export default {
  name: 'MsdatInstance',
  components: {
    ControlPanel,
    BaseIndicatorOverview,
  },
  data() {
    return {
      config: null,
      isMobile: false,
      show: true,
      position: 3,
      selectedPanel: 0,
      changeIndex: '',
      cpIsLoading: false,
      defaultIndicator: null,
      defaultDataSource: null,
      defaultLocation: null,
      defaultYear: null,
      controlConfig: [IndicatorOverviewConfig],
      yearsAsObjects: [],
      selectedSection: 'Indicator Overview',
      controlPanelSetup: [
        {
          type: 'dropdown',
          class: ['col-md-4'],
          dropdownProps: {
            'group-values': 'indicators',
            'group-label': 'program_area',
            label: 'short_name',
            trackBy: 'id',
            searchable: true,
            'allow-empty': false,
            'close-on-select': true,
            'clear-on-select': false,
            'preserve-search': true,
            'preselect-first': true,
          },
          label: 'Indicator',
          key: 'indicator',
          options: [],
        },
        {
          class: ['col'],
          type: 'dropdown',
          label: 'Data Source',
          key: 'datasource',
          dropdownProps: {
            label: 'datasource',
            trackBy: 'id',
            searchable: true,
            'allow-empty': false,
            'close-on-select': true,
            'clear-on-select': false,
            'preserve-search': true,
            'preselect-first': true,
          },
          options: [],
        },
        {
          class: ['col'],
          type: 'dropdown',
          label: 'Location',
          key: 'location',
          dropdownProps: {
            label: 'name',
            trackBy: 'id',
            searchable: true,
            'allow-empty': false,
            'close-on-select': true,
            'clear-on-select': false,
            'preserve-search': true,
            'preselect-first': true,
          },
          options: [],
        },
        {
          class: ['col'],
          type: 'dropdown',
          label: 'Year',
          key: 'year',
          options: [],
          dropdownProps: {
            'preselect-first': true,
            trackBy: 'value',
            label: 'text',
            searchable: true,
            'allow-empty': false,
            'close-on-select': true,
            'clear-on-select': false,
            'preserve-search': true,
          },
        },
      ],
    };
  },
  computed: {
    instanceStyles() {
      if (!this.config) return {};
      return {
        '--msdat-primary': this.config.theme.colors.primary,
        '--msdat-secondary': this.config.theme.colors.secondary,
        '--msdat-accent': this.config.theme.colors.accent,
        '--msdat-background': this.config.theme.colors.background,
        '--msdat-text': this.config.theme.colors.text,
      };
    },
    indicatorOverviewProps() {
      if (!this.config) {
        return {
          indicator: null,
          datasource: null,
          location: null,
          year: null,
          compareBy: '',
          visualization: 'state_map',
          target: {
            national: true,
            sdg: true,
          },
          numdenum: false,
        };
      }

      const defaultIndicator = this.defaultIndicator || this.config.indicators[0];
      const defaultDataSource = this.defaultDataSource || this.config.dataSources[0];
      const defaultLocation = this.defaultLocation || this.config.locations[0];
      const defaultYear = this.defaultYear || this.yearsAsObjects[0];

      // Ensure indicator has all required properties
      const indicator = {
        ...defaultIndicator,
        national_target: defaultIndicator.national_target || null,
        sdg_target: defaultIndicator.sdg_target || null,
        id: defaultIndicator.id,
        short_name: defaultIndicator.short_name,
        program_area: defaultIndicator.program_area,
        datasources: defaultIndicator.datasources || [],
      };

      return {
        indicator,
        datasource: defaultDataSource,
        location: defaultLocation,
        year: defaultYear,
        compareBy: '',
        visualization: 'state_map',
        target: {
          national: true,
          sdg: true,
        },
        numdenum: false,
      };
    },
    controlPanelProps() {
      const storePayload = this.$store.state.MSDAT_STORE.controlConfig[0]?.payload;
      if (!storePayload) return {};

      // Ensure indicator has all required properties
      const indicator = storePayload.indicator ? {
        ...storePayload.indicator,
        national_target: storePayload.indicator.national_target || null,
        sdg_target: storePayload.indicator.sdg_target || null,
        id: storePayload.indicator.id,
        short_name: storePayload.indicator.short_name,
        program_area: storePayload.indicator.program_area,
        datasources: storePayload.indicator.datasources || [],
      } : null;

      return {
        ...storePayload,
        indicator,
      };
    },
  },
  created() {
    // Load configuration from URL or localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const configParam = urlParams.get('config');
    if (configParam) {
      try {
        this.config = JSON.parse(decodeURIComponent(configParam));
        // Save to localStorage for persistence
        localStorage.setItem('msdatCurrentConfig', configParam);
      } catch (error) {
        console.error('Error parsing configuration:', error);
        this.loadFromLocalStorage();
      }
    } else {
      this.loadFromLocalStorage();
    }

    // Set default values from configuration
    if (this.config) {
      // Find the indicator object that matches the default indicator ID
      const defaultIndicatorObj = this.config.indicators.find((ind) => ind.id === this.config.defaultSettings.initialIndicator);
      this.defaultIndicator = defaultIndicatorObj || this.config.indicators[0];

      // Find the data source object that matches the default data source ID
      const defaultDataSourceObj = this.config.dataSources.find((ds) => ds.id === this.config.defaultSettings.initialDataSource);
      this.defaultDataSource = defaultDataSourceObj || this.config.dataSources[0];

      // Find the location object that matches the default location ID
      const defaultLocationObj = this.config.locations.find((loc) => loc.id === this.config.defaultSettings.initialLocation);
      this.defaultLocation = defaultLocationObj || this.config.locations[0];

      // Set default year as an object
      const yearsAsObjects = this.config.years.map((year) => (
        typeof year === 'object' ? year : { value: year, text: year.toString() }
      ));
      const defaultYearObj = yearsAsObjects.find((yearObj) => yearObj.value === this.config.defaultSettings.initialYear);
      this.defaultYear = defaultYearObj || yearsAsObjects[0];
      this.yearsAsObjects = yearsAsObjects; // store for use in control panel config

      // Group indicators by program area
      const groupedIndicators = this.config.indicators.reduce((acc, indicator) => {
        const programArea = indicator.program_area || 'Other';
        if (!acc[programArea]) {
          acc[programArea] = [];
        }
        acc[programArea].push({
          ...indicator,
          value: indicator.id,
          text: indicator.short_name,
        });
        return acc;
      }, {});

      // Convert grouped indicators to array format expected by vue-multiselect
      const indicatorOptions = Object.entries(groupedIndicators).map(([programArea, indicators]) => ({
        program_area: programArea,
        indicators,
      }));

      // Update control panel options
      this.controlPanelSetup[0].options = indicatorOptions;
      this.controlPanelSetup[1].options = this.config.dataSources.map((ds) => ({
        ...ds,
        value: ds.id,
        text: ds.datasource,
      }));
      this.controlPanelSetup[2].options = this.config.locations.map((loc) => ({
        ...loc,
        value: loc.id,
        text: loc.name,
      }));
      this.controlPanelSetup[3].options = yearsAsObjects;

      // Initialize control panel configuration
      const controlPanelConfig = {
        label: 'Indicator Overview',
        setup: this.controlPanelSetup,
        payload: {
          indicator: this.defaultIndicator,
          datasource: this.defaultDataSource,
          location: this.defaultLocation,
          year: this.defaultYear,
        },
      };

      // Add the control panel configuration to the Vuex store
      this.$store.commit('MSDAT_STORE/ADD_CONTROL_PANEL', controlPanelConfig);

      // Set initial values in the store
      this.$store.commit('MSDAT_STORE/SET_PAYLOAD', {
        controlIndex: 0,
        key: 'indicator',
        value: this.defaultIndicator,
      });
      this.$store.commit('MSDAT_STORE/SET_PAYLOAD', {
        controlIndex: 0,
        key: 'datasource',
        value: this.defaultDataSource,
      });
      this.$store.commit('MSDAT_STORE/SET_PAYLOAD', {
        controlIndex: 0,
        key: 'location',
        value: this.defaultLocation,
      });
      this.$store.commit('MSDAT_STORE/SET_PAYLOAD', {
        controlIndex: 0,
        key: 'year',
        value: this.defaultYear,
      });
    }

    // Apply theme to document
    this.applyTheme();
  },
  methods: {
    ...mapMutations('MSDAT_STORE', ['ADD_CONTROL_PANEL', 'CLEAR_CONTROL_PANEL']),
    loadFromLocalStorage() {
      const savedConfig = localStorage.getItem('msdatCurrentConfig');
      if (savedConfig) {
        try {
          this.config = JSON.parse(savedConfig);
        } catch (error) {
          console.error('Error loading configuration from localStorage:', error);
          this.loadDefaultConfig();
        }
      } else {
        this.loadDefaultConfig();
      }
    },
    loadDefaultConfig() {
      this.config = { ...defaultConfig };
    },
    applyTheme() {
      if (this.config?.theme) {
        document.documentElement.style.setProperty('--primary-color', this.config.theme.primaryColor);
        document.documentElement.style.setProperty('--secondary-color', this.config.theme.secondaryColor);
        document.documentElement.style.setProperty('--accent-color', this.config.theme.accentColor);
        document.documentElement.style.setProperty('--text-color', this.config.theme.textColor);
        document.documentElement.style.setProperty('--background-color', this.config.theme.backgroundColor);
      }
    },
    modifyLabel(label) {
      return label.toLowerCase().replace(/\s+/g, '_');
    },
    changeKey(key) {
      this.changeIndex = key;
    },
    sectionFocus(index) {
      this.selectedPanel = index;
    },
    handleValueChange(value) {
      console.log('Value changed:', value);
    },
    handleKeyChange(key) {
      console.log('Key changed:', key);
    },
    handleReset() {
      console.log('Reset triggered');
    },
    handleControlPanelUpdate(data) {
      if (!data) return;

      // Update the Vuex store with the new values
      Object.entries(data).forEach(([key, value]) => {
        if (value) {
          this.$store.commit('MSDAT_STORE/SET_PAYLOAD', {
            controlIndex: 0,
            key,
            value,
          });
        }
      });

      // Update local state if needed
      if (data.indicator) this.defaultIndicator = data.indicator;
      if (data.datasource) this.defaultDataSource = data.datasource;
      if (data.location) this.defaultLocation = data.location;
      if (data.year) this.defaultYear = data.year;
    },
  },
  watch: {
    config: {
      handler() {
        this.applyTheme();
      },
      deep: true,
    },
  },
};
</script>

<style>
/* Base styles */
.msdat-instance {
  min-height: 100vh;
  background-color: var(--msdat-background, #FFFFFF);
  color: var(--msdat-text, #2C3E50);
  display: flex;
  flex-direction: column;
}

/* Header styles */
#the-header {
  background-color: var(--msdat-primary);
  color: white;
  top: 0;
  left: 0;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

#the-header .main-text {
  color: white;
}

#the-header .main-text div {
  font-size: 1.2rem;
  font-weight: bold;
  opacity: 1;
}

#the-header .main-text small {
  font-size: 1rem;
  opacity: 0.8;
}

#the-header .user-account {
  color: white;
}

#the-header .user-account .dropdown-toggle {
  color: white;
  text-decoration: none;
}

#the-header .user-account .dropdown-toggle:hover {
  color: rgba(255, 255, 255, 0.8);
}

#the-header .user-account .dropdown-menu {
  background-color: white;
  border-color: var(--msdat-primary);
}

#the-header .user-account .dropdown-item:hover {
  background-color: var(--msdat-primary);
  color: white;
}

/* Main content wrapper */
.main-content-wrapper {
  margin-top: 80px; /* Height of header */
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Dashboard content layout */
.dashboard-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* Control panel styles */
.control-panel-container {
  width: 100%;
  position: sticky;
  top: 80px; /* Height of header */
  background-color: white;
  border-bottom: 1px solid var(--msdat-primary);
  z-index: 999;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.control-panel {
  padding: 1rem;
  background-color: white;
}

/* Main content area */
.main-content {
  padding: 1rem;
  flex: 1;
  overflow-y: auto;
}

/* Footer styles */
#the-footer {
  background-color: var(--msdat-primary);
  color: white;
  padding: 1rem 0;
  text-align: center;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
}

#the-footer .ml-3 {
  margin-left: 1rem;
}

/* Theme variables */
:root {
  --primary: var(--msdat-primary, #007D53);
  --secondary: var(--msdat-secondary, #348481);
  --accent: var(--msdat-accent, #FFB800);
  --background: var(--msdat-background, #FFFFFF);
  --text: var(--msdat-text, #2C3E50);
  --baseCardHeader: var(--msdat-secondary, #348481);
  --baseCard: var(--msdat-background, #FFFFFF);
  --MSDAT_COLOR_GRAY: var(--msdat-background, #FFFFFF);
}

/* Global theme overrides */
:deep(.btn-primary),
:deep(.bg-primary),
:deep(.text-primary),
:deep(.border-primary) {
  background-color: var(--msdat-primary) !important;
  border-color: var(--msdat-primary) !important;
  color: var(--msdat-primary) !important;
}

:deep(.btn-secondary),
:deep(.bg-secondary),
:deep(.text-secondary),
:deep(.border-secondary) {
  background-color: var(--msdat-secondary) !important;
  border-color: var(--msdat-secondary) !important;
  color: var(--msdat-secondary) !important;
}

:deep(.card-header) {
  background-color: var(--msdat-secondary) !important;
}

:deep(.card) {
  background-color: var(--msdat-background) !important;
}

/* Responsive styles */
@media (max-width: 768px) {
  .control-panel-container {
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 1001;
  }

  .main-content {
    padding: 1rem;
  }
}
</style>
